import type { AiLabelGeometry } from './aiLabelSpec'

/**
 * Builds the AI-disclosure label as a standalone SVG at its exact intrinsic
 * size. Shared by the backend baker (rasterised via the Playwright service) and
 * the frontend preview overlay, so both draw byte-identical geometry.
 *
 * Design notes:
 * - A dark translucent pill with white text reads on any underlying image,
 *   which a plain text overlay does not.
 * - `textLength` + `lengthAdjust` pin the text to the width the spec reserved,
 *   so the label can never clip regardless of which font resolves.
 * - The font stack is system-first; the label must not depend on a webfont
 *   loading inside the rasteriser.
 */
export type AiLabelColors = {
  backgroundColor: string
  textColor: string
  backgroundAlpha: number
}

export function buildAiLabelSvg(text: string, g: AiLabelGeometry, colors?: AiLabelColors): string {
  const fontStack =
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

  // Colours arrive from user settings, so they are constrained to a hex literal
  // before being interpolated into markup. Anything else falls back to the
  // default rather than reaching the SVG.
  const background = safeHex(colors?.backgroundColor, '#000000')
  const foreground = safeHex(colors?.textColor, '#ffffff')
  const alpha = clamp01(colors?.backgroundAlpha ?? g.backgroundAlpha)

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${g.width}" height="${g.height}" `,
    `viewBox="0 0 ${g.width} ${g.height}">`,
    `<rect x="0" y="0" width="${g.width}" height="${g.height}" rx="${g.radius}" ry="${g.radius}" `,
    `fill="${background}" fill-opacity="${alpha}"/>`,
    `<text x="${g.paddingX}" y="${g.height / 2}" `,
    `textLength="${g.textWidth}" lengthAdjust="spacingAndGlyphs" `,
    `font-family="${fontStack}" font-size="${g.fontSize}" font-weight="600" `,
    `fill="${foreground}" dominant-baseline="central">`,
    escapeXmlText(text),
    `</text>`,
    `</svg>`,
  ].join('')
}

/** Only `#rgb` / `#rrggbb` reaches the markup; anything else uses the default. */
function safeHex(value: string | undefined, fallback: string): string {
  return value && /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value) ? value : fallback
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 1
  return Math.min(1, Math.max(0, value))
}

/** Minimal XML text escaping. The label copy is ours, but it flows through a
 *  settings field, so never interpolate it raw. */
function escapeXmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
